/**
 * Typed property interfaces for all ComponentType variants.
 * Replaces unsafe component property casts throughout the AI chat hooks.
 *
 * Rules:
 * - All interfaces extend BaseComponentProperties (which allows arbitrary extra keys via index sig)
 * - ComponentPropertiesMap keys are ComponentType enum values
 * - Index signature `[key: string]: BaseComponentProperties` covers future types
 */

import type { ChartDataSource } from "./chartDataSource";
import type { ComponentDataBinding } from "./componentDataBinding";
import { ComponentType } from "./index";
import type { Component } from "./index";
import { isContainerType } from "@/utils/componentTypes";

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

export interface BaseComponentProperties {
  _templateInstanceId?: string;
  _templateNodeId?: string;
  _semanticRole?: string;
  _automationRuleListDescription?: string;
  content?: string;
  dataSource?: ChartDataSource;
  dataBinding?: ComponentDataBinding;
  fontSize?: number | string;
  height?: number | string;
  size?: number | string;
  selectedPinId?: string;
  tooltip?: {
    text: string;
    enabled?: boolean;
    side?: "top" | "right" | "bottom" | "left";
    delayDuration?: number;
  };
  useDataText?: boolean;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Text primitives
// ---------------------------------------------------------------------------

export interface TextComponentProperties extends BaseComponentProperties {
  text?: string;
  content?: string;
  fontSize?: number | string;
  fontWeight?: string;
  color?: string;
  title?: string;
  name?: string;
  label?: string;
}

export interface DataTextComponentProperties extends TextComponentProperties {
  dataSource?: ChartDataSource;
  data?: unknown[];
}

// ---------------------------------------------------------------------------
// Interactive primitives
// ---------------------------------------------------------------------------

export interface ButtonComponentProperties extends BaseComponentProperties {
  label?: string;
  variant?: string;
  onClick?: string;
  disabled?: boolean;
}

export interface ImageComponentProperties extends BaseComponentProperties {
  src?: string;
  alt?: string;
  objectFit?: string;
}

export interface InputComponentProperties extends BaseComponentProperties {
  placeholder?: string;
  label?: string;
  type?: string;
  value?: string;
  required?: boolean;
}

export interface IconComponentProperties extends BaseComponentProperties {
  iconName?: string;
  iconLibrary?: string;
  iconSize?: number;
  iconColor?: string;
  rotation?: number;
}

// ---------------------------------------------------------------------------
// Chart types
// ---------------------------------------------------------------------------

export interface ChartDataPoint {
  label?: string;
  value?: number;
  name?: string;
  [key: string]: unknown;
}

export interface ChartComponentProperties extends BaseComponentProperties {
  title?: string;
  data?: ChartDataPoint[];
  dataSource?: ChartDataSource;
  xAxisKey?: string;
  yAxisKey?: string;
  lineKeys?: string[];
  xAxis?: string;
  yAxis?: string;
  minHeight?: number | string;
  flex?: string;
}

// ---------------------------------------------------------------------------
// Map chart (extends chart, adds pins)
// ---------------------------------------------------------------------------

export interface MapPinStyle {
  color?: string;
  icon?: string;
  size?: number;
  [key: string]: unknown;
}

export interface MapPinNavigation {
  pageId?: string;
  url?: string;
  action?: string;
  [key: string]: unknown;
}

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  label?: string;
  style?: MapPinStyle;
  navigation?: MapPinNavigation;
  group?: string;
  [key: string]: unknown;
}

export interface MapChartComponentProperties extends ChartComponentProperties {
  pins?: MapPin[];
  layers?: Record<string, unknown>;
  center?: { lat: number; lng: number };
  zoom?: number;
}

// ---------------------------------------------------------------------------
// Container types
// ---------------------------------------------------------------------------

export interface ContainerComponentProperties extends BaseComponentProperties {
  minHeight?: number | string;
  padding?: number | string;
  gap?: number | string;
  direction?: "row" | "column";
}

// ---------------------------------------------------------------------------
// Fallback
// ---------------------------------------------------------------------------

export type GenericComponentProperties = BaseComponentProperties;

// ---------------------------------------------------------------------------
// Union of all known property types
// ---------------------------------------------------------------------------

export type AnyComponentProperties =
  | TextComponentProperties
  | DataTextComponentProperties
  | ButtonComponentProperties
  | ImageComponentProperties
  | InputComponentProperties
  | ChartComponentProperties
  | MapChartComponentProperties
  | ContainerComponentProperties
  | IconComponentProperties
  | GenericComponentProperties;

// ---------------------------------------------------------------------------
// Discriminated map — keys are ComponentType enum string values
// ---------------------------------------------------------------------------

export type ComponentPropertiesMap = {
  // Text primitives
  [ComponentType.TEXT]: TextComponentProperties;
  [ComponentType.DATA_TEXT]: DataTextComponentProperties;

  // Interactive primitives
  [ComponentType.BUTTON]: ButtonComponentProperties;
  [ComponentType.IMAGE]: ImageComponentProperties;
  [ComponentType.INPUT]: InputComponentProperties;
  [ComponentType.ICON]: IconComponentProperties;

  // Charts (all share ChartComponentProperties base)
  [ComponentType.CHART]: ChartComponentProperties;
  [ComponentType.BAR_CHART]: ChartComponentProperties;
  [ComponentType.LINE_CHART]: ChartComponentProperties;
  [ComponentType.PIE_CHART]: ChartComponentProperties;
  [ComponentType.SCATTER_CHART]: ChartComponentProperties;
  [ComponentType.AREA_CHART]: ChartComponentProperties;
  [ComponentType.HEATMAP_CHART]: ChartComponentProperties;
  [ComponentType.TREEMAP_CHART]: ChartComponentProperties;
  [ComponentType.GAUGE_CHART]: ChartComponentProperties;
  [ComponentType.VERTICAL_GAUGE_CHART]: ChartComponentProperties;
  [ComponentType.MAP_CHART]: MapChartComponentProperties;

  // Data widgets
  [ComponentType.DATA_CARD]: ChartComponentProperties;
  [ComponentType.RADIAL_DIAL]: ChartComponentProperties;
  [ComponentType.LEVEL_INDICATOR]: ChartComponentProperties;

  // Containers
  [ComponentType.FLEX_CONTAINER]: ContainerComponentProperties;
  [ComponentType.CONTAINER]: ContainerComponentProperties;
  [ComponentType.CARD]: ContainerComponentProperties;
  [ComponentType.FORM_CONTAINER]: ContainerComponentProperties;
  [ComponentType.MODAL]: ContainerComponentProperties;

  // Data display
  [ComponentType.TABLE]: GenericComponentProperties;
  [ComponentType.ADVANCED_TABLE]: GenericComponentProperties;
  [ComponentType.LIST]: GenericComponentProperties;

  // Controls
  [ComponentType.TOGGLE_SWITCH]: GenericComponentProperties;
  [ComponentType.PUSH_BUTTON]: ButtonComponentProperties;
  [ComponentType.ICON_BUTTON]: ButtonComponentProperties;
  [ComponentType.STATUS_LIGHT]: GenericComponentProperties;
  [ComponentType.SLIDER]: GenericComponentProperties;
  [ComponentType.STEPPER]: GenericComponentProperties;
  [ComponentType.SEGMENTED_CONTROL]: GenericComponentProperties;
  [ComponentType.COLOR_PICKER]: GenericComponentProperties;
  [ComponentType.THERMOSTAT]: GenericComponentProperties;

  // Complex widgets
  [ComponentType.KANBAN_BOARD]: GenericComponentProperties;
  [ComponentType.CHECKLIST]: GenericComponentProperties;
  [ComponentType.HORIZONTAL_TIMELINE]: GenericComponentProperties;
  [ComponentType.ASSIGNMENT_WIDGET]: GenericComponentProperties;
  [ComponentType.GANTT_CHART]: GenericComponentProperties;
  [ComponentType.CALENDAR]: GenericComponentProperties;
  [ComponentType.PROJECT_HUB]: GenericComponentProperties;
  [ComponentType.QUESTIONNAIRE]: GenericComponentProperties;
  [ComponentType.VOTING_CARD]: GenericComponentProperties;
  [ComponentType.TIME_TRACKER]: GenericComponentProperties;
  [ComponentType.BREADCRUMB]: GenericComponentProperties;
  [ComponentType.FILE_STORAGE]: GenericComponentProperties;
  [ComponentType.FERMENTATION_TANK_CARD]: GenericComponentProperties;
  [ComponentType.CUSTOM]: GenericComponentProperties;

  // Index signature — covers any additional ComponentType values
  [key: string]: BaseComponentProperties;
};

export type TypedComponent<T extends keyof ComponentPropertiesMap> = Omit<
  Component,
  "type" | "properties"
> & {
  type: T;
  properties: ComponentPropertiesMap[T];
};

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export const CHART_TYPES = new Set<ComponentType>([
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.AREA_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.MAP_CHART,
  ComponentType.DATA_CARD,
  ComponentType.RADIAL_DIAL,
  ComponentType.LEVEL_INDICATOR,
]);

export function isChartComponent(
  c: Component,
): c is Component & { properties: ChartComponentProperties } {
  return CHART_TYPES.has(c.type);
}

export function isButtonComponent(
  c: Component,
): c is Component & { properties: ButtonComponentProperties } {
  return (
    c.type === ComponentType.BUTTON ||
    c.type === ComponentType.PUSH_BUTTON ||
    c.type === ComponentType.ICON_BUTTON
  );
}

export function isTextComponent(
  c: Component,
): c is Component & { properties: TextComponentProperties } {
  return c.type === ComponentType.TEXT || c.type === ComponentType.DATA_TEXT;
}

export function isInputComponent(
  c: Component,
): c is Component & { properties: InputComponentProperties } {
  return c.type === ComponentType.INPUT;
}

export function isMapChartComponent(
  c: Component,
): c is Component & { properties: MapChartComponentProperties } {
  return c.type === ComponentType.MAP_CHART;
}

/**
 * Delegates to the existing utility — do NOT duplicate the container type set here.
 */
export function isContainerLikeComponent(
  c: Component,
): c is Component & { properties: ContainerComponentProperties } {
  return isContainerType(c.type);
}

/**
 * Narrows `component.properties` to the precise type for the given ComponentType.
 * Use with components whose `type` is already known statically.
 */
export function narrowProperties<T extends keyof ComponentPropertiesMap>(
  component: TypedComponent<T>,
): ComponentPropertiesMap[T] {
  return component.properties;
}
